import {
	type DeliveryMethod,
	EXPRESS_COURIER_DAYS,
	EXPRESS_COURIER_PROVIDER,
	type ExpressCourierDay,
} from "@dransay/api/models";

/**
 * Lieferando operating-window copy for the header outside-hours card and the
 * cart's disabled-time line. Uber reads today's pharmacy window instead.
 * Hardcoded pending confirmation from Platform Integrations (see the
 * "Operating-window copy conflict" open question in
 * docs/features/express-courier-delivery/behavior.md).
 */
export const EXPRESS_COURIER_OPERATING_HOURS = {
	startTime: "07:00",
	endTime: "16:00",
} as const;

type ExpressCourierDayWindow = NonNullable<DeliveryMethod["days"]>[number];

const CLOCK_HOURS_MINUTES = /^(\d{2}):(\d{2})/;

function isExpressCourierDay(value: string): value is ExpressCourierDay {
	return (EXPRESS_COURIER_DAYS as readonly string[]).includes(value);
}

function hoursAndMinutes(value: string): string | null {
	const match = CLOCK_HOURS_MINUTES.exec(value);
	if (!match) {
		return null;
	}
	return `${match[1]}:${match[2]}`;
}

function berlinExpressCourierDay(now: Date): ExpressCourierDay | null {
	const name = new Intl.DateTimeFormat("en-US", {
		timeZone: "Europe/Berlin",
		weekday: "long",
	})
		.format(now)
		.toLowerCase();
	return isExpressCourierDay(name) ? name : null;
}

/** Uber's order window is opens-at through shipping-closes-at, in Berlin. */
export function expressCourierDisabledTimeHours(
	provider: string | null | undefined,
	days: readonly ExpressCourierDayWindow[] | null | undefined,
	now: Date = new Date(),
): { startTime: string; endTime: string } {
	if (provider !== EXPRESS_COURIER_PROVIDER.UBER_DIRECT) {
		return EXPRESS_COURIER_OPERATING_HOURS;
	}
	const today = berlinExpressCourierDay(now);
	const window = days?.find((day) => day.day === today && day.enabled);
	const startTime = window ? hoursAndMinutes(window.from) : null;
	const endTime = window?.shippingClosesAt
		? hoursAndMinutes(window.shippingClosesAt)
		: null;
	if (!startTime || !endTime) {
		return EXPRESS_COURIER_OPERATING_HOURS;
	}
	return { startTime, endTime };
}

export function expressCourierProviderName(
	provider?: string | null,
): string | null {
	if (provider === EXPRESS_COURIER_PROVIDER.UBER_DIRECT) {
		return "Uber";
	}
	if (provider === EXPRESS_COURIER_PROVIDER.LIEFERANDO) {
		return "Lieferando";
	}
	return null;
}
